import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";
import { OPEN_POSITION_RESOURCE } from "./use-open-position-search.query";

export const OPEN_POSITION_SEARCH_BY_CUSTOMER_OPERATION = "search/by-customer" as const;
export const OPEN_POSITION_SEARCH_BY_CUSTOMER_API_PATH =
  `/v1/${OPEN_POSITION_RESOURCE}/${OPEN_POSITION_SEARCH_BY_CUSTOMER_OPERATION}` as const;

export type OpenPositionAggregateSearchParameters =
  paths[typeof OPEN_POSITION_SEARCH_BY_CUSTOMER_API_PATH]["post"]["parameters"]["query"];
export type OpenPositionAggregateSearchBody =
  paths[typeof OPEN_POSITION_SEARCH_BY_CUSTOMER_API_PATH]["post"]["requestBody"]["content"]["application/json"];

export type OpenPositionCustomerAggregateSearchResponse =
  paths[typeof OPEN_POSITION_SEARCH_BY_CUSTOMER_API_PATH]["post"]["responses"][200]["content"]["application/json"];
export type OpenPositionCustomerAggregate = OpenPositionCustomerAggregateSearchResponse["data"][number];

export function useOpenPositionCustomerAggregateSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<OpenPositionAggregateSearchParameters, OpenPositionAggregateSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    OpenPositionCustomerAggregateSearchResponse,
    Error,
    OpenPositionCustomerAggregateSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<
      PaginatedOptions<OpenPositionAggregateSearchParameters, OpenPositionAggregateSearchBody>
    >(options);
  const params = handlePaginatedParameters(optionsWithDefaults.parameters);
  const body = optionsWithDefaults.body;

  return useQuery({
    queryKey: [OPEN_POSITION_RESOURCE, OPEN_POSITION_SEARCH_BY_CUSTOMER_OPERATION, { params, body }],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<OpenPositionCustomerAggregateSearchResponse>(
        OPEN_POSITION_SEARCH_BY_CUSTOMER_API_PATH,
        body,
        { params },
      );

      return response.data;
    },
    staleTime: 15_000,
    ...queryOptions,
    enabled,
  });
}
